import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import api from "../utils/api";
import { IRISH_COUNTIES } from "../utils/irishCounties";

const getDeliveryAddressFields = (formData) => ({
  billingAddressLine1: formData.addressLine1,
  billingAddressLine2: formData.addressLine2,
  billingCity: formData.city,
  billingState: formData.state,
  billingPostalCode: formData.postalCode,
  billingCountry: formData.country,
});

const isBillingSameAsDelivery = (formData) => {
  const deliveryAddressFields = getDeliveryAddressFields(formData);

  return Object.entries(deliveryAddressFields).every(([billingKey, value]) => formData[billingKey] === value);
};

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "",
  billingAddressLine1: "",
  billingAddressLine2: "",
  billingCity: "",
  billingState: "",
  billingPostalCode: "",
  billingCountry: "",
};

const AccountDetails = () => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [billingSameAsDelivery, setBillingSameAsDelivery] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await api.get("/auth/profile");
        const user = response.data.user || {};

        const nextFormData = {
          firstName: user.first_name || "",
          lastName: user.last_name || "",
          email: user.email || "",
          phone: user.phone || "",
          addressLine1: user.address_line1 || "",
          addressLine2: user.address_line2 || "",
          city: user.city || "",
          state: user.state || "",
          postalCode: user.postal_code || "",
          country: user.country || "",
          billingAddressLine1: user.billing_address_line1 || "",
          billingAddressLine2: user.billing_address_line2 || "",
          billingCity: user.billing_city || "",
          billingState: user.billing_state || "",
          billingPostalCode: user.billing_postal_code || "",
          billingCountry: user.billing_country || "",
        };

        setFormData(nextFormData);
        setBillingSameAsDelivery(isBillingSameAsDelivery(nextFormData));
      } catch (error) {
        console.error("Error fetching profile:", error);
        toast.error("Failed to load your details");
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (!billingSameAsDelivery) {
      return;
    }

    setFormData((current) => {
      const nextBillingFields = getDeliveryAddressFields(current);
      const hasChanges = Object.entries(nextBillingFields).some(([billingKey, value]) => current[billingKey] !== value);

      if (!hasChanges) {
        return current;
      }

      return {
        ...current,
        ...nextBillingFields,
      };
    });
  }, [billingSameAsDelivery, formData.addressLine1, formData.addressLine2, formData.city, formData.state, formData.postalCode, formData.country]);

  const handleBillingSameAsDeliveryChange = (event) => {
    const checked = event.target.checked;
    setBillingSameAsDelivery(checked);

    if (!checked) {
      return;
    }

    setFormData((current) => ({
      ...current,
      ...getDeliveryAddressFields(current),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      const payload = billingSameAsDelivery
        ? {
            ...formData,
            ...getDeliveryAddressFields(formData),
          }
        : formData;

      await api.put("/auth/profile", payload);
      toast.success("Your details have been updated");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(error.response?.data?.error || "Failed to update your details");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center text-gold-500">Loading your details...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-8 sm:py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="heading-metallic mb-2 text-3xl sm:text-4xl">Account Details</h1>
          <p className="text-gray-400">Update your contact and address information.</p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          onSubmit={handleSubmit}
          className="space-y-8 rounded-lg border border-gray-800 bg-gradient-to-r from-black via-gray-900 to-black p-4 sm:p-6"
        >
          <div>
            <h2 className="heading-metallic-soft text-2xl mb-6">Customer Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">First Name</label>
                <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} className="input-field w-full" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Last Name</label>
                <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} className="input-field w-full" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Email Address</label>
                <input type="email" name="email" value={formData.email} readOnly className="input-field w-full opacity-70 cursor-not-allowed" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Phone Number</label>
                <input type="text" name="phone" value={formData.phone} onChange={handleChange} className="input-field w-full" />
              </div>
            </div>
          </div>

          <div>
            <h2 className="heading-metallic-soft text-2xl mb-6">Delivery Address</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">Address Line 1</label>
                <input type="text" name="addressLine1" value={formData.addressLine1} onChange={handleChange} className="input-field w-full" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">Address Line 2</label>
                <input type="text" name="addressLine2" value={formData.addressLine2} onChange={handleChange} className="input-field w-full" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">City</label>
                <input type="text" name="city" value={formData.city} onChange={handleChange} className="input-field w-full" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">County</label>
                <select name="state" value={formData.state} onChange={handleChange} className="input-field w-full">
                  <option value="">-- Select County --</option>
                  {IRISH_COUNTIES.map((county) => (
                    <option key={county} value={county}>
                      {county}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Postal Code</label>
                <input type="text" name="postalCode" value={formData.postalCode} onChange={handleChange} className="input-field w-full" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Country</label>
                <input type="text" name="country" value={formData.country} onChange={handleChange} className="input-field w-full" />
              </div>
            </div>
          </div>

          <div>
            <h2 className="heading-metallic-soft text-2xl mb-6">Billing Address</h2>
            <label className="mb-4 flex items-center gap-3 rounded border border-gray-700 bg-black/30 px-4 py-3 text-sm text-white">
              <input type="checkbox" checked={billingSameAsDelivery} onChange={handleBillingSameAsDeliveryChange} />
              <span>Billing address same as delivery address</span>
            </label>
            {!billingSameAsDelivery && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Address Line 1</label>
                  <input type="text" name="billingAddressLine1" value={formData.billingAddressLine1} onChange={handleChange} className="input-field w-full" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Address Line 2</label>
                  <input type="text" name="billingAddressLine2" value={formData.billingAddressLine2} onChange={handleChange} className="input-field w-full" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">City</label>
                  <input type="text" name="billingCity" value={formData.billingCity} onChange={handleChange} className="input-field w-full" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">County</label>
                  <select name="billingState" value={formData.billingState} onChange={handleChange} className="input-field w-full">
                    <option value="">-- Select County --</option>
                    {IRISH_COUNTIES.map((county) => (
                      <option key={county} value={county}>
                        {county}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Postal Code</label>
                  <input type="text" name="billingPostalCode" value={formData.billingPostalCode} onChange={handleChange} className="input-field w-full" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Country</label>
                  <input type="text" name="billingCountry" value={formData.billingCountry} onChange={handleChange} className="input-field w-full" />
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-stretch sm:justify-end">
            <button type="submit" disabled={saving} className="btn-primary w-full sm:min-w-[11rem] sm:w-auto">
              {saving ? "Saving..." : "Save Details"}
            </button>
          </div>
        </motion.form>
      </div>
    </div>
  );
};

export default AccountDetails;